import React, { useEffect, useRef, useState } from 'react';
import { SimpleReveal } from './SimpleReveal';

interface VideoPhoneMockupProps {
  videoUrl?: string;
}

export function VideoPhoneMockup({ videoUrl = "https://vimeo.com/1125218543" }: VideoPhoneMockupProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  // Extract Vimeo video ID from URL
  const getVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  const vimeoId = getVimeoId(videoUrl);
  const embedUrl = vimeoId 
    ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&muted=1&background=1`
    : videoUrl;

  // Fetch Vimeo thumbnail
  useEffect(() => {
    if (!vimeoId) return;
    
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`)
      .then(res => res.json())
      .then(data => {
        if (data.thumbnail_url) {
          setThumbnailUrl(data.thumbnail_url);
        }
      })
      .catch(err => console.error('Failed to fetch Vimeo thumbnail:', err));
  }, [vimeoId]);

  return (
    <div className="relative w-full max-w-[320px] lg:max-w-[370px] mx-auto transition-all duration-500">
      {/* Radial Glow Behind Phone */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[600px] -z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(168, 85, 247, 0.25) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }}
      />

      {/* Phone Mockup */}
      <div className="relative">
        {/* iPhone Frame */}
        <div className="relative bg-white rounded-[3rem] p-3 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)]">
          {/* Notch */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-white rounded-b-3xl z-10" />
          
          {/* Screen */}
          <div className="relative bg-black rounded-[2.5rem] overflow-hidden aspect-[9/19.5]">
            {/* Vimeo Thumbnail Poster with Shimmer Effect */}
            {thumbnailUrl && (
              <div className="absolute inset-0 bg-cover bg-center"
                   style={{ backgroundImage: `url(${thumbnailUrl})` }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" 
                     style={{
                       backgroundSize: '200% 100%',
                       animation: 'shimmer 2s infinite'
                     }} />
              </div>
            )}
            
            {/* Video iframe loads on top */}
            <iframe
              src={embedUrl}
              className="absolute left-1/2 -translate-x-1/2 w-[160%] h-[160%] -top-[28%] pointer-events-none"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Artist Engine video showcase"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
